import React, {RefObject, useRef} from 'react';
import {
    UserMenuPopper,
    UserMenuOptions,
    UserMenuItem,
    UserMenuSeparator,
    BackIcon,
    LinkedUserMenuItem
} from './UserMenu.style';
import {checkIsExternalLink, createSolutionRoute} from "../../../../utils/url.service";
import { Translations } from '../../../common/models/common.models';
import useData from 'shell/dataContext';
import { useFocusTrap } from "../../../common/hooks/useTrapFocus";

interface ILicenseMenuOptionProps {
    type: ELicenseTypes;
    onUpgradeLicense?: () => void;
    onPurchaseLicense?: () => void;
    translations: Translations;
}

export enum ELicenseTypes {
    NO_LICENSE,
    STANDARD_LICENSE,
    ADVANCED_LICENSE,
}

type UserMenuItem = {
    text: string;
    linkTo: string;
}

const LicenseMenuOption = ({ type, onUpgradeLicense, onPurchaseLicense, translations }: ILicenseMenuOptionProps) => {
    switch (type) {
        case ELicenseTypes.STANDARD_LICENSE:
            return (
                <UserMenuItem onClick={onUpgradeLicense}>
                    {translations.TI2020_ContentTranslations_Header_UpgradeLicense}
                </UserMenuItem>
            );
        case ELicenseTypes.ADVANCED_LICENSE:
            return null;
        default:
            return (
                <UserMenuItem onClick={onPurchaseLicense}>
                    {translations.TI2020_ContentTranslations_Header_PurchaseLicense}
                </UserMenuItem>
            );
    }
};

interface IUserMenuProps {
    items: UserMenuItem[];
    licenseType: ELicenseTypes;
    translations: Translations;
    onSignOut: () => void;
    clientName: string;
    onClientSwitchContext: () => void;
    onPurchaseLicense: () => void;
    onUpgradeLicense: () => void;
    popperRef: RefObject<any>;
    baseUrl: string;
    handleFocusParent: () => void;
}

export const UserMenu = (props: IUserMenuProps) => {
    const { items, licenseType, translations, onSignOut, clientName, onClientSwitchContext, popperRef, handleFocusParent } = props;
    const {isHosted} = useData() ?? {};

    const userMenuRef = useRef<HTMLDivElement | null>(null);

    const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            action();
        }
    };

    useFocusTrap({
        containerRef: userMenuRef,
        onEscape: () => {
            popperRef.current.toggle();
            handleFocusParent();
        },
    });

    return (
        <UserMenuPopper ref={userMenuRef}>
            <UserMenuOptions>
                {items.map((item, index) => {
                    if (checkIsExternalLink(item.linkTo) || !isHosted) {
                        return (<UserMenuItem
                            key={index}
                            href={item.linkTo}
                            disabled={item.linkTo === ''}
                            onClick={() => popperRef.current.toggle()}
                        >
                            {item.text}
                        </UserMenuItem>);
                    }
                    return (<LinkedUserMenuItem
                        tabIndex={0}
                        key={index}
                        to={createSolutionRoute(item.linkTo)}
                        onClick={() => popperRef.current.toggle()}
                        disabled={item.linkTo === ''}
                    >
                        {item.text}
                    </LinkedUserMenuItem>);

                })}
            </UserMenuOptions>
            <LicenseMenuOption {...props} type={licenseType} />
            <UserMenuSeparator />
            {
                isHosted
                    ? <LinkedUserMenuItem
                        tabIndex={0}
                        to= {"/solution-explorer/custom-survey"}
                        onClick={() => popperRef.current.toggle()}>
                        {translations.TI2020_ContentTranslations_Header_UserMenu_SolutionExplorer}
                    </LinkedUserMenuItem>
                    : <UserMenuItem
                        href= {`${window.tcp.tsDomain}solution-explorer/custom-survey`}
                        onClick={() => popperRef.current.toggle()}>
                        {translations.TI2020_ContentTranslations_Header_UserMenu_SolutionExplorer}
                    </UserMenuItem>
            }
            <UserMenuSeparator />
            {clientName == null ? (
                <UserMenuItem tabIndex={0} onClick={onSignOut} onKeyDown={(event) => handleKeyDown(event, onSignOut)}>
                    {translations.TI2020_ContentTranslations_Header_UserMenu_SignOut}
                </UserMenuItem>
            ) : (
                <UserMenuItem tabIndex={0} onClick={onClientSwitchContext} onKeyDown={(event) => handleKeyDown(event, onClientSwitchContext)}>
                    <BackIcon />
                    {translations.TI2020_ContentTranslations_Header_UserMenu_BackToUserSide}
                </UserMenuItem>
            )}
        </UserMenuPopper>
    );
};
